import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Download, FileText, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const XMLDownload = () => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [arquivos, setArquivos] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();

  // Função para formatar valores em Real
  const formatReal = (value) => {
    if (!value) return '0,00';
    
    const int = parseInt(value.toFixed(2).toString().replace(/[^\d]+/g, ''));
    let tmp = int.toString();
    tmp = tmp.replace(/([0-9]{2})$/g, ",$1");
    if (tmp.length > 6) {
      tmp = tmp.replace(/([0-9]{3}),([0-9]{2}$)/g, ".$1,$2");
    }
    tmp = tmp.replace(".", "");

    if (tmp === "0") {
      tmp = "0,00";
    }

    return `R$ ${tmp}`;
  };

  // Função para buscar arquivos
  const buscarArquivos = async () => {
    if (!token.trim()) {
      toast.error("Informe o Token de acesso");
      return;
    }

    setLoading(true);
    
    try {
      const uri = `https://gatewaysistemas.com.br/GatewayApi/api/arquivo/RetornarMesAno?ticket=${token}`;
      const response = await axios.get(uri);
      
      if (response.data && response.data.length > 0) {
        setArquivos(response.data);
        setShowResults(true);
        toast.success("Arquivos encontrados com sucesso!");
        
        // Scroll suave para os resultados
        setTimeout(() => {
          const resultsElement = document.getElementById('divResultado');
          if (resultsElement) {
            resultsElement.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        toast.warning("Não existem registros para o Token informado!");
        setShowResults(false);
        setArquivos([]);
      }
    } catch (error) {
      console.error('Erro ao buscar arquivos:', error);
      toast.error("Não foi possível realizar a consulta");
      setShowResults(false);
      setArquivos([]);
    } finally {
      setLoading(false);
    }
  };

  // Função para fazer download do arquivo
  const downloadArquivo = async (arquivoBase64) => {
    setLoading(true);
    
    try {
      const uri = `https://gatewaysistemas.com.br/GatewayApi/api/arquivo/DownloadArquivo?ticket=${arquivoBase64}`;
      const response = await axios.get(uri);
      
      if (response.data) {
        const downloadUrl = `https://gatewaysistemas.com.br/GatewayApi/api/arquivo/Download?arquivo=${response.data}`;
        window.open(downloadUrl, '_blank');
        toast.success("Download iniciado com sucesso!");
      } else {
        toast.error("Não foi possível realizar o download do arquivo!");
      }
    } catch (error) {
      console.error('Erro no download:', error);
      toast.error("Erro ao processar o download");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      buscarArquivos();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="mr-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar ao Site
              </Button>
              <img 
                src="https://customer-assets.emergentagent.com/job_eda8ca4d-c957-4325-a9ab-0a26f5df1d41/artifacts/2jmiflv8_Gateway_500_200_2_C.png" 
                alt="Gateway Sistemas" 
                className="h-8 w-auto"
              />
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Download de Arquivos XML
          </h1>
          <p className="text-xl text-gray-600">
            Acesse e baixe seus arquivos XML utilizando seu token de acesso
          </p>
        </div>

        {/* Token Input Section */}
        <Card className="max-w-2xl mx-auto mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="mr-2 h-5 w-5" />
              Acesso aos Arquivos
            </CardTitle>
            <CardDescription>
              Digite seu token de acesso para visualizar os arquivos XML disponíveis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="token">Token de Acesso</Label>
              <Input
                id="token"
                type="text"
                placeholder="Digite seu token aqui..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
                onKeyPress={handleKeyPress}
                className="text-center"
              />
            </div>
            
            <Button 
              onClick={buscarArquivos} 
              disabled={loading || !token.trim()}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Consultando...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Acessar Arquivos
                </>
              )}
            </Button>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Importante:</strong> Certifique-se de ter o token correto fornecido pela Gateway Sistemas.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Results Section */}
        {showResults && (
          <div id="divResultado" className="max-w-6xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Arquivos Disponíveis</span>
                  <Badge variant="secondary">
                    {arquivos.length} arquivo(s) encontrado(s)
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Clique no botão de download para baixar o arquivo XML correspondente
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/3">Período (Ano/Mês)</TableHead>
                        <TableHead className="w-1/3">Valor</TableHead>
                        <TableHead className="w-1/3 text-center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {arquivos.map((arquivo, index) => (
                        <TableRow key={index} className="hover:bg-gray-50">
                          <TableCell className="font-medium">
                            {arquivo.ano_mes}
                          </TableCell>
                          <TableCell className="text-green-600 font-semibold">
                            {formatReal(arquivo.Valor)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="sm"
                              onClick={() => downloadArquivo(arquivo.ArquivoBase64)}
                              disabled={loading}
                              className="bg-yellow-500 hover:bg-yellow-600 text-black"
                            >
                              {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="mr-2 h-4 w-4" />
                                  Baixar
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Help Section */}
        <Card className="max-w-4xl mx-auto mt-12">
          <CardHeader>
            <CardTitle>Precisa de Ajuda?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">Como obter o token?</h4>
                <p className="text-gray-600 text-sm">
                  O token de acesso é fornecido pela Gateway Sistemas. Entre em contato conosco 
                  se você não possui um token ou se está tendo problemas para acessar.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Problemas com download?</h4>
                <p className="text-gray-600 text-sm">
                  Certifique-se de que seu navegador permite downloads e que não há bloqueios 
                  de pop-up ativados. Os arquivos são baixados automaticamente.
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <Button 
                variant="outline" 
                onClick={() => window.open('https://api.whatsapp.com/send?1=pt_BR&phone=5515991089431', '_blank')}
              >
                Suporte via WhatsApp
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default XMLDownload;